/**
 * `solutionFor(scheme)` — the correct answer to every item, in the shape the asset speaks.
 *
 * This is the whole of solution mode's server side. A host that wants to show a learner the
 * answers calls it on the scheme it already stores (host obligation 2) and puts the result in
 * `Init.solution`. The scheme itself stays where it has always been, which is not the browser.
 *
 * **Why the output is `ItemResponse` and not the scheme item.** The asset drew the controls, so
 * it already knows how to render a response — it builds one every time the learner answers. So
 * "show the solution" is "render this as though the learner had given it", which needs no new
 * per-type protocol and works for all five item types the day each is added. The alternative,
 * handing over `ItemSchemeDoc`, would put `points`, `credit`, `optionCount`, feedback and hints
 * in the frame in order to draw a tick — grading machinery shipped to render an answer.
 *
 * Pure, like everything else in this package: no I/O, no clock, no randomness, no locale-
 * sensitive comparison. It takes no locale at all, and that is not an omission — a solution is
 * ids plus, for `textEntry`, one accepted string. There is no authored sentence in it to resolve.
 *
 * The inverse of `score()`, and deliberately not fused with it: scoring compares a learner's
 * response against the scheme, and this reads the scheme alone. Nothing here sees a response.
 */

import type { ItemResponse, ItemSchemeDoc, SchemeDoc } from '@cet/asset-contract';

/**
 * Every item's correct answer, keyed by item id — the exact shape `Init.solution` carries.
 *
 * Every item in the scheme appears. An item with no expressible answer would be an item nobody
 * could be marked on, and there is no such type today: all five carry their answer in the
 * scheme, which is why the file is never published.
 */
export function solutionFor(scheme: SchemeDoc): Record<string, ItemResponse> {
  const solution: Record<string, ItemResponse> = {};

  // Sorted for the same reason `score()` sorts: two callers must produce the same object from
  // the same scheme, and JSON key order is not something to rest that on.
  for (const itemId of Object.keys(scheme.items).sort()) {
    solution[itemId] = responseFor(scheme.items[itemId]!);
  }

  return solution;
}

/**
 * One scheme item to the response that would have been correct.
 *
 * Exhaustive over `ItemSchemeDoc` with no `default` branch, so adding a sixth item type fails
 * to compile here rather than silently omitting its answer — the same reason `isItemResponse`
 * is one list. A missing entry would be indistinguishable from an item with no solution, and
 * the asset would draw nothing with nothing reporting why (D39).
 */
function responseFor(item: ItemSchemeDoc): ItemResponse {
  switch (item.type) {
    case 'choice':
      return { choiceId: item.correct };
    case 'multiChoice':
      return { choiceIds: item.correct };
    case 'ordering':
      return { order: item.correct };
    case 'matching':
      return { pairs: item.correct };
    case 'textEntry':
      /**
       * The first accepted string, by convention.
       *
       * `accept` is a list of answers that all mark correct — "Jerusalem", "jerusalem",
       * a spelling variant — and a solution display needs exactly one of them. Nothing in the
       * scheme distinguishes a canonical form from an accepted alternative today, so the
       * convention is that the author writes the one they would show first. It is documented
       * for authors in AGENTS.md; if it ever needs to be explicit the answer is a `display`
       * field on the scheme item, which is additive (D27).
       *
       * `accept` is non-empty by schema — an item accepting nothing could never be answered —
       * so the fallback below is unreachable and exists only so the type is honest.
       */
      // A judged item may accept nothing exactly (D93): its model answer is then the one to show.
      return { text: item.accept[0] ?? item.judge?.model ?? '' };
    case 'numeric':
      /**
       * **The first type whose answer is not unique**, which is why it carries `display` and the
       * other five do not. An interval has infinitely many correct answers and nothing here can
       * derive one, so the author writes the example they want shown.
       *
       * The fallback repeats `min`, and it is worth being clear that this is a *fallback and not
       * a derivation*: under `distinct` it is an answer that scores one field out of however
       * many, so solution mode would display something demonstrably wrong. A contract rule
       * therefore requires `display` for every item whose answer is not unique — anything but
       * `min === max` with a single field — and this line is reachable only for the case where
       * repeating `min` is exactly right, plus the invalid asset the rule already rejected.
       */
      return {
        values:
          item.display !== undefined
            ? [...item.display]
            : Array.from({ length: item.fields ?? 1 }, () => String(item.min)),
      };
  }
}
